import { AlertTriangle, ArrowUpRight, CalendarDays, FileText, Layers3 } from "lucide-react";
import type { JSX } from "react";

import { formatDate } from "@/lib/format";
import type { ReportImageBlobFetcher, ReportRef, ReportRevisionRead } from "@/lib/reports";
import { groupReportVisualRelationsByBlock, reportImageFetcherNotWired } from "@/lib/reports";

import { ReportBlock } from "./ReportBlock";
import { type ReportThemeOverride, reportThemeStyle } from "./report-theme";

export type ExecutiveReportMode = "dock" | "expanded" | "standalone";

interface ExecutiveReportProps {
  read: ReportRevisionRead;
  themeOverride: ReportThemeOverride;
  mode: ExecutiveReportMode;
  onOpenRevision?: (report: ReportRef) => void;
  onOpenSource?: (handle: string) => void;
  /**
   * TODO(reports-images-task-6): defaults to the not-wired placeholder
   * (`reportImageFetcherNotWired`) because Task 4's backend audience byte
   * routes don't exist yet. Every real page (`ReportReader.tsx`,
   * `shared-report.tsx`, `shared-team-report.tsx`) passes this explicitly so
   * Task 6 can find and replace exactly those three call sites.
   */
  fetchImageBlob?: ReportImageBlobFetcher;
}

const DEPTH_LABEL = { brief: "Brief", standard: "Estándar", deep: "Profundo" } as const;

export function ExecutiveReport({
  read,
  themeOverride,
  mode,
  onOpenRevision,
  onOpenSource,
  fetchImageBlob = reportImageFetcherNotWired,
}: ExecutiveReportProps): JSX.Element {
  const { revision, newer_revision: newerRevision } = read;
  const { definition } = revision;
  const generatedDate = formatDate(revision.created_at);
  // `revision.visual_assets` is a FLAT, block/position-ordered list (no
  // `handle`) — group it once per render into per-block slices so each
  // `ReportBlock` gets exactly its own `image` block's relations, in
  // asset-ref order (see `groupReportVisualRelationsByBlock` in
  // `@/lib/reports` for why this positional grouping is the correlation
  // contract instead of a `handle` field).
  const imageRelationsByBlock = groupReportVisualRelationsByBlock(
    definition,
    revision.visual_assets,
  );

  return (
    <article
      className="min-h-full bg-[var(--report-bg)] text-[var(--report-fg)]"
      data-report-depth={definition.depth}
      data-report-mode={mode}
      data-report-theme={themeOverride}
      style={reportThemeStyle(themeOverride)}
    >
      <header className="relative overflow-hidden border-b border-[var(--report-border)] bg-[var(--report-fg)] px-5 py-8 text-[var(--report-bg)] sm:px-8">
        <div className="absolute inset-y-0 right-0 w-1/3 bg-[radial-gradient(circle_at_center,var(--report-accent)_0,transparent_68%)] opacity-25" />
        <div className="relative">
          <div className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] opacity-75">
            <FileText size={14} /> Executive Brief
          </div>
          <h1 className="max-w-3xl text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
            {definition.title}
          </h1>
          {definition.subtitle ? (
            <p className="mt-3 max-w-2xl text-base leading-7 opacity-75">{definition.subtitle}</p>
          ) : null}
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs opacity-75">
            <span className="inline-flex items-center gap-1.5">
              <Layers3 size={14} /> v{revision.revision_number}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays size={14} />{" "}
              {definition.as_of ? formatDate(definition.as_of) : generatedDate}
            </span>
            <span>{DEPTH_LABEL[definition.depth]}</span>
          </div>
        </div>
      </header>

      {revision.status === "partial" ? (
        <div className="flex gap-3 border-b border-[var(--report-border)] bg-[var(--report-accent-soft)] px-5 py-3 text-sm sm:px-8">
          <AlertTriangle className="mt-0.5 shrink-0 text-[var(--report-risk)]" size={16} />
          Algunas secciones solicitadas no pudieron incluirse. El contenido validado disponible
          sigue visible.
        </div>
      ) : null}

      {newerRevision ? (
        <output
          aria-label="Revisión disponible"
          className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--report-border)] bg-[var(--report-muted)] px-5 py-3 text-sm sm:px-8"
        >
          <span>Hay una revisión más reciente de este informe.</span>
          <button
            className="inline-flex items-center gap-1 font-semibold text-[var(--report-info)] hover:underline"
            onClick={() => onOpenRevision?.(newerRevision)}
            type="button"
          >
            Abrir v{newerRevision.revision_number} <ArrowUpRight size={14} />
          </button>
        </output>
      ) : null}

      <div
        className={
          mode === "expanded"
            ? "mx-auto grid max-w-[1440px] gap-8 px-5 py-7 lg:grid-cols-[190px_minmax(0,760px)_240px] lg:px-8"
            : "mx-auto max-w-3xl px-5 py-6 sm:px-8"
        }
      >
        {mode === "expanded" ? (
          <nav
            aria-label="Secciones del informe"
            className="hidden self-start lg:sticky lg:top-6 lg:block"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[var(--report-muted-fg)]">
              Contenido
            </p>
            <ol className="space-y-1 text-sm">
              {definition.blocks.map((block) => (
                <li key={block.block_id}>
                  <a
                    className="block rounded-md px-2 py-1.5 text-[var(--report-muted-fg)] hover:bg-[var(--report-muted)] hover:text-[var(--report-fg)]"
                    href={`#report-section-${block.block_id}`}
                  >
                    {block.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        ) : (
          <nav aria-label="Secciones del informe" className="sr-only">
            {definition.blocks.map((block) => (
              <a href={`#report-section-${block.block_id}`} key={block.block_id}>
                {block.heading}
              </a>
            ))}
          </nav>
        )}

        <main className="min-w-0">
          {definition.blocks.map((block) => (
            <ReportBlock
              block={block}
              key={block.block_id}
              sources={revision.sources}
              imageRelations={imageRelationsByBlock.get(block.block_id) ?? []}
              fetchImageBlob={fetchImageBlob}
            />
          ))}
        </main>

        <section
          aria-label="Fuentes"
          className={
            mode === "expanded"
              ? "self-start border-t border-[var(--report-border)] pt-5 lg:sticky lg:top-6 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0"
              : "border-t border-[var(--report-border)] pt-6"
          }
        >
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--report-muted-fg)]">
            Fuentes
          </h2>
          {revision.sources.length ? (
            <ol className="mt-3 space-y-3 text-sm">
              {revision.sources.map((source, index) => (
                <li key={source.handle}>
                  <button
                    className="w-full rounded-lg border border-[var(--report-border)] p-3 text-left hover:bg-[var(--report-muted)]"
                    onClick={() => onOpenSource?.(source.handle)}
                    type="button"
                  >
                    <span className="block text-xs text-[var(--report-muted-fg)]">{index + 1}</span>
                    <span className="mt-1 block font-medium">{source.title}</span>
                    {source.preview ? (
                      <span className="mt-1 block max-h-16 overflow-hidden text-xs leading-5 text-[var(--report-muted-fg)]">
                        {source.preview}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-3 text-sm text-[var(--report-muted-fg)]">Sin fuentes visibles.</p>
          )}
        </section>
      </div>
    </article>
  );
}
